import { BlockingMode, type MaintenanceSlot } from '@/lib/types/maintenance';
import { getErrorMessage } from '@/plugins/i18n/enums';
import { useMaintenancesStore } from '@/store/maintenance';
import { useResourcesStore } from '@/store/resource';
import { atEndOfDay } from '@/utils/date';
import { useToast } from 'primevue/usetoast';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { MaintenanceSlotForm } from './useMaintenancesPage';

// Maximum number of conflicting visits listed in the error banner (UI-SPEC contract 7). Five rows plus the
// overflow line keep the banner under ~130px inside a dialog whose slot list is already max-h-[60vh]; ten rows
// would push the Save button off screen on a laptop. Exported so the composable and the template share one number.
export const CONFLICT_LIST_CAP = 5;

export function useMaintenanceScheduleDialog() {
    const { t } = useI18n();
    const toast = useToast();
    const maintenanceStore = useMaintenancesStore();
    const resourceStore = useResourcesStore();

    // State
    const dialogVisible = ref(false);
    const saving = ref(false);
    const validationError = ref<string | null>(null);
    const selectedResourceId = ref<string | null>(null);
    // Conflicting visits returned by a rejected save (MAINT-05 / D-15). Never truncated here — the display cap
    // lives in the template so the "…and N more" count stays accurate.
    const conflicts = ref<MaintenanceConflictRow[]>([]);

    const form = reactive({
        slots: [] as MaintenanceSlotForm[]
    });

    const selectedResourceData = computed(() => {
        if (!selectedResourceId.value) return null;
        return resourceStore.resources.find((r) => r.id === selectedResourceId.value);
    });

    const resourceName = computed(() => selectedResourceData.value?.name || '');
    const resourceType = computed(() => selectedResourceData.value?.type.name || '');
    // Single source for the D-05 visibility gate, the capacity input's upper bound and the hint's {max}, so the
    // three can never disagree.
    const resourceCapacity = computed(() => selectedResourceData.value?.capacity ?? 0);

    // Helper functions
    function toTimestamp(date: Date | null): number | null {
        if (!date) return null;
        return date.getTime();
    }

    function parseTimestamp(timestamp: number): Date {
        return new Date(timestamp);
    }

    // Store time as HH:mm (no seconds)
    function timeToFullFormat(time: string | null): string {
        if (!time) return '00:00';
        if (/^\d{2}:\d{2}$/.test(time)) return time;
        if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time.substring(0, 5);
        return '00:00';
    }

    // Returns the canonical HH:mm for the two shapes timeToFullFormat accepts, or null for anything else.
    // Null means "the operator typed something this form cannot send", and saveMaintenance turns that into a
    // visible validation error rather than letting timeToFullFormat guess '00:00'.
    function formatTimeInput(input: string | null): string | null {
        if (!input) return null;
        const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(input);
        if (!match) return null;

        const h = Number(match[1]);
        const m = Number(match[2]);
        if (h < 0 || h > 23 || m < 0 || m > 59) return null;

        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    function addSlot() {
        form.slots.push({
            title: '',
            description: null,
            dayOfWeek: 'Monday',
            startTime: '09:00',
            endTime: '17:00',
            validFromDate: null,
            validUntilDate: null,
            blockingMode: BlockingMode.FullyBlocking,
            reducedCapacity: null
        });
    }

    function removeSlot(index: number) {
        form.slots.splice(index, 1);
    }

    function updateSlot(index: number, field: string, value: any) {
        if (field in form.slots[index]) {
            (form.slots[index] as any)[field] = value;
        }
    }

    // The mode switch is not a plain field assignment — it has to prefill or clear the capacity as a side effect,
    // so it deliberately does not go through the generic updateSlot.
    function setBlockingMode(index: number, mode: BlockingMode) {
        const slot = form.slots[index];
        if (!slot) return;

        slot.blockingMode = mode;

        if (mode === BlockingMode.PartiallyBlocking) {
            // 1 is the most restrictive valid value, so an unnoticed default can never silently widen a resource.
            if (slot.reducedCapacity == null) {
                slot.reducedCapacity = 1;
            }
            return;
        }

        // Load-bearing: the database CHECK rejects FullyBlocking with a non-null capacity. The save serialization
        // and mapMaintenanceSlotToApi are the second and third lines of defence, not the first.
        slot.reducedCapacity = null;
    }

    async function openSchedule(resourceId: string) {
        validationError.value = null;
        conflicts.value = [];
        selectedResourceId.value = resourceId;

        try {
            await maintenanceStore.fetchByResourceId(resourceId);
            const slots = maintenanceStore.getSlotsByResourceId(resourceId);

            form.slots = slots ? slots.slots.map(slot => ({
                title: slot.title,
                description: slot.description,
                dayOfWeek: slot.dayOfWeek,
                startTime: slot.start ? slot.start.substring(0, 5) : '',
                endTime: slot.end ? slot.end.substring(0, 5) : '',
                validFromDate: slot.validFrom ? parseTimestamp(slot.validFrom) : null,
                validUntilDate: slot.validUntil ? parseTimestamp(slot.validUntil) : null,
                blockingMode: slot.blockingMode,
                reducedCapacity: slot.reducedCapacity
            })) : [];

            dialogVisible.value = true;
        } catch (error) {
            console.error('Error loading maintenance slots:', error);
            validationError.value = t('maintenance.validation.loadFailed');
        }
    }

    function closeDialog() {
        dialogVisible.value = false;
        selectedResourceId.value = null;
    }

    async function saveMaintenance(onSuccess?: () => void) {
        validationError.value = null;
        conflicts.value = [];

        if (!selectedResourceId.value) {
            validationError.value = t('maintenance.validation.resourceRequired');
            return;
        }

        // Validate each slot
        for (const slot of form.slots) {
            if (!slot.title.trim()) {
                validationError.value = t('maintenance.validation.titleRequired');
                return;
            }
            if (!slot.startTime || !slot.endTime) {
                validationError.value = t('maintenance.validation.timesRequired');
                return;
            }

            // The payload is built with timeToFullFormat, which returns '00:00' for anything it cannot parse —
            // including '9:00', which this validation used to accept because it split the raw string. The window
            // was then silently widened to start at midnight. Normalize here, refuse the save when the value is
            // unusable, and write the canonical form back so the field shows exactly what will be sent.
            const normalizedStart = formatTimeInput(slot.startTime);
            const normalizedEnd = formatTimeInput(slot.endTime);
            if (!normalizedStart || !normalizedEnd) {
                validationError.value = t('maintenance.validation.timesRequired');
                return;
            }

            slot.startTime = normalizedStart;
            slot.endTime = normalizedEnd;

            const [startHour, startMin] = normalizedStart.split(':').map(Number);
            const [endHour, endMin] = normalizedEnd.split(':').map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (startMinutes >= endMinutes) {
                validationError.value = t('maintenance.validation.endTimeAfterStartTime');
                return;
            }
            if (slot.validFromDate && slot.validUntilDate && slot.validFromDate > slot.validUntilDate) {
                validationError.value = t('maintenance.validation.validUntilAfterValidFrom');
                return;
            }

            // MAINT-02, client half. The control's own :min/:max and these two checks are a usability layer in
            // front of the server-side rejection (plan 03-03), which is the actual gate and is reachable without
            // this dialog. Neither replaces the other.
            if (slot.blockingMode === BlockingMode.PartiallyBlocking) {
                if (slot.reducedCapacity == null || typeof slot.reducedCapacity !== 'number' || Number.isNaN(slot.reducedCapacity)) {
                    validationError.value = t('maintenance.validation.reducedCapacityRequired');
                    return;
                }
                if (slot.reducedCapacity < 1 || slot.reducedCapacity > resourceCapacity.value) {
                    validationError.value = t('maintenance.validation.reducedCapacityRange', { max: resourceCapacity.value });
                    return;
                }
            }
        }

        saving.value = true;
        try {
            const slotsToSave: MaintenanceSlot[] = form.slots.map(slot => ({
                title: slot.title,
                description: slot.description,
                dayOfWeek: slot.dayOfWeek,
                start: timeToFullFormat(slot.startTime),
                end: timeToFullFormat(slot.endTime),
                validFrom: toTimestamp(slot.validFromDate),
                validUntil: toTimestamp(atEndOfDay(slot.validUntilDate)),
                blockingMode: slot.blockingMode,
                reducedCapacity: slot.blockingMode === BlockingMode.PartiallyBlocking ? slot.reducedCapacity : null
            }));

            const error = await maintenanceStore.setMaintenanceSlots({
                resourceId: selectedResourceId.value,
                slots: slotsToSave
            });

            if (error) {
                // D-16: the early return below keeps dialogVisible true and form.slots untouched, so the operator
                // fixes the offending row and resubmits. Do not turn it into a throw and do not close the dialog.
                validationError.value = getErrorMessage(error, t);
                conflicts.value = error.conflicts ?? [];
                return;
            }

            dialogVisible.value = false;
            selectedResourceId.value = null;

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error saving maintenance:', error);
            validationError.value = t('maintenance.validation.saveFailed');
        } finally {
            saving.value = false;
        }
    }

    return {
        // State
        dialogVisible,
        saving,
        validationError,
        conflicts,
        selectedResourceId,
        form,
        resourceName,
        resourceType,
        resourceCapacity,

        // Actions
        addSlot,
        removeSlot,
        updateSlot,
        setBlockingMode,
        openSchedule,
        closeDialog,
        saveMaintenance
    };
}
